/**
 * Adapter: wires the chosen subscription offering through the unified
 * `<CheckoutShell>` in picker-required mode.
 *
 * Responsibilities:
 *   1. Read the chosen selection from SubscriptionCheckoutContext.
 *      If null (refresh / direct nav), bounce back to the landing page.
 *   2. Load the company's saved payment sources on mount.
 *   3. Build a Cart from the selection via buildSubscriptionCart.
 *   4. Wire onAddPaymentSource → createPaymentSource API helper.
 *   5. Wire onSubmit → createSubscription API helper (sends canonical
 *      caraSku + frequency, NOT raw price), then navigate to the
 *      routable confirmation page.
 *
 * What stays domain-specific (NOT in this file):
 *   - Pre-checkout: the landing page owns the marketing copy + picker.
 *   - <SubscriptionConfirmationPage> at /subscriptions/:id/confirmation.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CheckoutShell } from "@/modules/checkout/components/CheckoutShell";
import type {
  AcceptJsToken,
  PaymentSource,
  SubmitInput,
  SubmitResult,
} from "@/modules/checkout/types";
import { listPaymentSources } from "@/modules/checkout/api/listPaymentSources";
import { createPaymentSource } from "@/modules/checkout/api/createPaymentSource";

import { useSubscriptionCheckout } from "../../SubscriptionCheckoutContext";
import { buildSubscriptionCart } from "../../utils/buildSubscriptionCart";
import { createSubscription } from "../../api/createSubscription";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";

const ACCEPT_JS_DESCRIPTOR = "COMMON.ACCEPT.INAPP.PAYMENT";

export default function SubscriptionCheckoutShellAdapter() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { state } = useSubscriptionCheckout();
  const { selection } = state;

  const [paymentSources, setPaymentSources] = useState<PaymentSource[] | null>(
    null,
  );

  // Refresh on /checkout (or direct nav) leaves selection null. Bounce
  // back to the landing page where the user can pick again.
  useEffect(() => {
    if (selection === null) {
      navigate(`/subscriptions/${slug ?? ""}`, { replace: true });
    }
  }, [selection, navigate, slug]);

  // Hydrate the saved-card list once. The list grows in-memory after a
  // successful `onAddPaymentSource` save (the shell prepends + selects);
  // we don't need to re-fetch.
  useEffect(() => {
    if (selection === null) return;
    let cancelled = false;
    listPaymentSources()
      .then((response) => {
        if (!cancelled) setPaymentSources(response.data.paymentSources);
      })
      .catch(() => {
        if (!cancelled) setPaymentSources([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selection]);

  // Pre-bounce: render nothing while the redirect effect runs.
  if (selection === null) return null;

  // Wait for the saved-card list before rendering the shell so the
  // picker has its real initial state.
  if (paymentSources === null) {
    return <LoadingIndicator />;
  }

  const cart = buildSubscriptionCart({ selection });

  const onAddPaymentSource = async (
    token: AcceptJsToken,
  ): Promise<PaymentSource> => {
    const expiration = (token.cardExpiration ?? "").split("/");
    const expMonth = expiration[0] ?? "";
    const expYearShort = expiration[1] ?? "";
    const expYear =
      expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;

    const response = await createPaymentSource({
      paymentName: token.name !== "" ? token.name : null,
      paymentDescriptor: ACCEPT_JS_DESCRIPTOR,
      paymentToken: token.dataValue,
      paymentExpMonth: expMonth,
      paymentExpYear: expYear,
      paymentLast4: token.last4,
    });
    return response.data;
  };

  const onSubmit = async (input: SubmitInput): Promise<SubmitResult> => {
    if (input.payment?.kind !== "saved") {
      throw new Error("Subscription checkout requires a saved card.");
    }

    const response = await createSubscription({
      paymentSourceId: input.payment.paymentSourceId,
      caraSku: selection.caraSku,
      frequency: selection.frequency,
    });

    const successPath = `/subscriptions/${slug ?? ""}/confirmation/${response.data.subscriptionUuid}`;
    navigate(successPath);

    return {
      confirmationNumber: response.data.subscriptionId,
      successPath,
    };
  };

  return (
    <CheckoutShell
      cart={cart}
      onAddPaymentSource={onAddPaymentSource}
      onSubmit={onSubmit}
      paymentMode="picker-required"
      paymentSources={paymentSources}
    />
  );
}
